var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import inkFrag from "@/shaders/ink.frag";
import { range } from "lodash";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, } from "react";
import { ShaderMaterial, Vector4 } from "three";
import { useTextureWithCORS } from "../utils/three-loader-hooks";
import { useAtomValue } from "jotai";
import { Zoom } from "@/atoms";
import { clamp01, inverseLerp } from "@/utils/math";
import { MIN_ZOOM } from "@/components/CameraControl";
import { lerp } from "three/src/math/MathUtils";
import { CONFIG } from "@/config";
var DROPS = 7;
/**
 * Create a shader material that makes the page appear through several ink drops
 */
export var usePageInkShader = function (background, ambiance, foreground) {
    var zoom = useAtomValue(Zoom);
    var targetZoom = useRef(zoom);
    var ink = useTextureWithCORS(require("@/public_assets/ink_fx3.jpg").src);
    // Generate ink drops positions and sizes
    var inkDrop = useCallback(function (i) {
        // TODO replace with size of mesh relative to ink blot
        var _a = CONFIG.pages.targetRes, pageWidthOrig = _a.width, pageHeightOrig = _a.height;
        var pageHeight = 720;
        var pageWidth = (pageHeight / pageWidthOrig) * pageHeightOrig;
        var _b = ink.image, inkWidth = _b.width, inkHeight = _b.height;
        if (i === 6) {
            var w_1 = 1.75 + Math.random() * 0.2;
            var h_1 = 1.75 + Math.random() * 0.2;
            return new Vector4(0.5 - w_1 / 2, 0.5 - h_1 / 2, w_1, h_1);
        }
        var cols = 3;
        var rows = 2;
        var col = i % cols;
        var row = Math.floor(i / cols);
        var w = (inkWidth / pageWidth) * (1.1 + Math.random() * 0.1);
        var h = (inkHeight / pageHeight) * (1.1 + Math.random() * 0.1);
        var x = (col + 0.5 + (Math.random() - 0.5) * 1) / cols - w / 2;
        var y = (row + 0.5 + (Math.random() - 0.5) * 1) / rows - h / 2;
        return new Vector4(x, y, w, h);
    }, [ink.image, background.image]);
    // Generate ink drop speeds
    var inkDropSpeed = function (i) {
        if (i === 6) {
            return new Vector4(0.5, 1, Math.random() > 0.5 ? 1 : 0, Math.random() > 0.5 ? 1 : 0);
        }
        return new Vector4(Math.random() * 0.3, 1 + Math.random() * 1, Math.random() > 0.5 ? 1 : 0, Math.random() > 0.5 ? 1 : 0);
    };
    // Initialise the shader material
    var _a = __read(useState(function () {
        return new ShaderMaterial({
            fragmentShader: inkFrag,
            uniforms: {
                art: { value: foreground || background },
                page: { value: background },
                ambiance: { value: ambiance },
                ink: { value: ink },
                ambianceScale: { value: 1 },
                inkDrops: {
                    value: range(0, DROPS).map(inkDrop),
                },
                inkDropsSpeed: {
                    value: range(0, DROPS).map(inkDropSpeed),
                },
            },
        });
    }), 1), shader = _a[0];
    // Update page textures
    useLayoutEffect(function () {
        shader.uniforms.art.value = foreground || background;
    }, [shader, foreground, background]);
    // Update page textures
    useEffect(function () {
        targetZoom.current = clamp01(inverseLerp(0.2, MIN_ZOOM, zoom));
    }, [zoom]);
    useEffect(function () {
        function updateZoom() {
            shader.uniforms.ambianceScale.value = lerp(shader.uniforms.ambianceScale.value, targetZoom.current, 0.1);
            raf = requestAnimationFrame(updateZoom);
        }
        var raf = requestAnimationFrame(updateZoom);
        return function () {
            cancelAnimationFrame(raf);
        };
    }, [shader]);
    return shader;
};
